/**
 * Instruments the sounds share, built from the toolkit (sfx/kit.ts): a blade's whoosh, a guzheng
 * strum, a temple gong, a singing bowl, chimes, a woodblock, a drum, rustling paper. A family file
 * (sfx/sounds/*.ts) mixes these with the toolkit's own voices.
 */
import { BELL, type NoiseColor, type SfxKit } from './kit';

/** The gong mode of the Chinese pentatonic (宮商角徵羽), in semitones. */
export const PENTA = [0, 2, 4, 7, 9] as const;
/** The pentatonic around D, the music's home key (audio.ts). */
export const ROOT_D = 293.66;

/** The `deg`-th step of the pentatonic above `root` (negative steps go below). */
export function note(root: number, deg: number): number {
  const oct = Math.floor(deg / PENTA.length);
  const i = ((deg % PENTA.length) + PENTA.length) % PENTA.length;
  return root * 2 ** (oct + PENTA[i]! / 12);
}

/**
 * Air torn by something passing: band noise sweeping up as it nears and down as it leaves,
 * loudest a little before the middle.
 */
export function whoosh(
  k: SfxKit,
  o: {
    at?: number;
    dur: number;
    f0: number;
    f1: number;
    f2?: number;
    vol: number;
    q?: number;
    color?: NoiseColor;
    /** Where the peak falls, as a fraction of the length. */
    peak?: number;
    out?: AudioNode;
  },
): void {
  k.noise({
    color: o.color,
    at: o.at,
    dur: o.dur,
    vol: o.vol,
    attack: o.dur * (o.peak ?? 0.4),
    f: o.f0,
    f1: o.f1,
    sweep: o.dur * (o.f2 !== undefined ? (o.peak ?? 0.4) + 0.1 : 1),
    f2: o.f2,
    q: o.q ?? 2,
    out: o.out,
  });
}

/** A guzheng strum: the strings in turn, each a pressed-string bend falling into tune. */
export function strum(
  k: SfxKit,
  root: number,
  degrees: readonly number[],
  o: { at?: number; gap?: number; vol: number; dur?: number; bright?: number },
): void {
  degrees.forEach((d, i) =>
    k.pluck({
      f: note(root, d),
      at: (o.at ?? 0) + i * (o.gap ?? 0.045),
      dur: o.dur ?? 1.4,
      vol: o.vol * (1 - i * 0.04),
      bright: o.bright ?? 0.55,
      bend: 0.012,
    }),
  );
}

/** A temple gong: beating partials that drift flat as they fade, over a soft mallet thump. */
export function gong(k: SfxKit, o: { f: number; at?: number; dur: number; vol: number }): void {
  k.thump({ f0: o.f * 1.2, f1: o.f * 0.6, at: o.at, dur: 0.35, vol: o.vol * 0.7 });
  k.metal({ f: o.f, at: o.at, dur: o.dur, vol: o.vol, partials: BELL, drift: 0.012, attack: 0.012 });
  k.metal({
    f: o.f * 1.007,
    at: o.at,
    dur: o.dur * 0.8,
    vol: o.vol * 0.5,
    partials: [
      [1, 1, 1],
      [2.4, 0.5, 0.6],
    ],
    drift: 0.01,
    attack: 0.03,
  });
  // The shimmer that swells a moment after the strike.
  k.noise({
    color: 'pink',
    at: (o.at ?? 0) + 0.05,
    dur: o.dur * 0.6,
    vol: o.vol * 0.12,
    attack: 0.4,
    f: o.f * 5,
    q: 3,
  });
}

/** A singing bowl struck soft: a pure ring with slow beating. */
export function bowl(k: SfxKit, o: { f: number; at?: number; dur: number; vol: number }): void {
  k.tone({ f: o.f, at: o.at, dur: o.dur, vol: o.vol, attack: 0.01 });
  k.tone({ f: o.f * 1.004, at: o.at, dur: o.dur * 0.9, vol: o.vol * 0.6, attack: 0.02 });
  k.tone({ f: o.f * 2.71, at: o.at, dur: o.dur * 0.5, vol: o.vol * 0.25, attack: 0.01 });
  k.tone({ f: o.f * 5.1, at: o.at, dur: o.dur * 0.2, vol: o.vol * 0.08 });
}

/** A small bright chime (a bell on a string, jade on jade). */
export function chime(k: SfxKit, o: { f: number; at?: number; dur?: number; vol: number }): void {
  const dur = o.dur ?? 0.9;
  k.fm({ f: o.f, ratio: 3.5, index: 1.6, index1: 0.05, at: o.at, dur, vol: o.vol });
  k.tone({ f: o.f * 2, at: o.at, dur: dur * 0.5, vol: o.vol * 0.25 });
}

/** A woodblock (木魚) knock. */
export function woodblock(k: SfxKit, o: { f: number; at?: number; vol: number }): void {
  k.tone({ f: o.f, f1: o.f * 0.92, at: o.at, dur: 0.09, vol: o.vol, attack: 0.001 });
  k.noise({ at: o.at, dur: 0.03, vol: o.vol * 0.6, attack: 0.001, f: o.f * 2.2, q: 6 });
}

/** A drum: a skin dropping in pitch and the slap of the stick. */
export function drum(k: SfxKit, o: { f: number; at?: number; vol: number; dur?: number }): void {
  const dur = o.dur ?? 0.4;
  k.thump({ f0: o.f * 1.9, f1: o.f, at: o.at, dur, vol: o.vol });
  k.noise({ at: o.at, dur: 0.05, vol: o.vol * 0.35, attack: 0.001, f: 1400, q: 1.2 });
}

/** Paper or a silk scroll moving: crackle and a breath of high noise. */
export function paper(k: SfxKit, o: { at?: number; dur: number; vol: number; bright?: number }): void {
  const b = o.bright ?? 1;
  k.noise({
    color: 'crackle',
    at: o.at,
    dur: o.dur,
    vol: o.vol,
    attack: o.dur * 0.25,
    type: 'highpass',
    f: 1800 * b,
    q: 0.7,
  });
  k.noise({
    at: o.at,
    dur: o.dur,
    vol: o.vol * 0.35,
    attack: o.dur * 0.3,
    f: 4200 * b,
    f1: 2600 * b,
    q: 1.4,
  });
}

/** A blade ringing after a clash or a draw. */
export function ring(k: SfxKit, o: { f: number; at?: number; dur: number; vol: number }): void {
  k.metal({ f: o.f, at: o.at, dur: o.dur, vol: o.vol });
}
